import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Upload, message, Card, Space, Row, Col, Typography, Modal, Switch, Divider, Tag, Grid } from 'antd';
import { PlusOutlined, DeleteOutlined, StarOutlined, StarFilled, CopyOutlined, DownloadOutlined, EyeOutlined, ShoppingOutlined, CheckCircleOutlined, StopOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Product, ProductImage, ProductStatus } from '../../types';
import { mediaService } from '../../services/media.service';

import config from '../../config';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface ProductFormProps {
    initialValues?: Product;
    onSubmit: (values: any) => Promise<void>;
    loading?: boolean;
}

export const ProductForm: React.FC<ProductFormProps> = ({ initialValues, onSubmit, loading }) => {
    const [form] = Form.useForm();
    const [images, setImages] = useState<ProductImage[]>(initialValues?.images || []);
    const [uploading, setUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState<string>('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                status: initialValues.status || ProductStatus.IN_STOCK
            });
            setImages(initialValues.images || []);
        }
    }, [initialValues, form]);

    const getImageUrl = (url: string) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const formattedUrl = url.startsWith('/') ? url : `/${url}`;
        return `${config.BASE_URL}${formattedUrl}`;
    };

    const handleUpload = async ({ file, onSuccess, onError }: any) => {
        try {
            setUploading(true);
            const response = await mediaService.upload(file, 'products');
            const newImage: ProductImage = {
                url: response.url,
                filename: response.filename,
                isPrimary: false,
            };
            
            setImages(prev => {
                const hasPrimary = prev.some(img => img.isPrimary);
                return [...prev, { ...newImage, isPrimary: !hasPrimary && prev.length === 0 }];
            });

            onSuccess(response, file);
            message.success(`Đã tải lên tệp: ${file.name}`);
        } catch (error) {
            console.error('Upload failed', error);
            onError(error);
            message.error(`Không thể tải lên tệp: ${file.name}`);
        } finally {
            setUploading(false);
        }
    };

    const handleSetPrimary = (index: number) => {
        const newImages = images.map((img, i) => ({
            ...img,
            isPrimary: i === index,
        }));
        setImages(newImages);
    };

    const handleDeleteImage = (index: number) => {
        const newImages = images.filter((_, i) => i !== index);
        if (images[index].isPrimary && newImages.length > 0) {
            newImages[0].isPrimary = true;
        }
        setImages(newImages);
    };

    const handleCopyImage = async (url: string) => {
        const key = 'copying_image';
        try {
            message.loading({ content: 'Đang xử lý hình ảnh...', key });
            const fullUrl = getImageUrl(url);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = fullUrl;

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = () => reject(new Error('Failed to load image'));
            });

            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
                throw new Error('Canvas context unavailable');
            }
            
            ctx.drawImage(img, 0, 0);

            const blob = await new Promise<Blob | null>((resolve) => {
                canvas.toBlob(resolve, 'image/png');
            });

            if (!blob) {
                throw new Error('Canvas conversion failed');
            }

            await navigator.clipboard.write([
                new ClipboardItem({
                    'image/png': blob
                })
            ]);
            
            message.success({ content: 'Đã sao chép hình ảnh vào bộ nhớ tạm', key });
        } catch (err) {
            console.error('Failed to copy image:', err);
            message.error({ content: 'Không thể sao chép hình ảnh (vui lòng thử "Chuột phải > Sao chép")', key });
        }
    };

    const handleDownload = (url: string, filename?: string) => {
        const fullUrl = getImageUrl(url);
        const link = document.createElement('a');
        link.href = fullUrl;
        link.target = '_blank';
        link.download = filename || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const onFinish = (values: any) => {
        onSubmit({ ...values, images });
    };

    return (
        <Card bordered={false} bodyStyle={{ padding: isMobile ? '12px' : '24px' }} style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)' }}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                    status: ProductStatus.IN_STOCK,
                    ...initialValues
                }}
            >
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={15}>
                        <section style={{ marginBottom: 24 }}>
                            <Title level={4} style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                                Thông tin cơ bản
                            </Title>
                            
                            <Form.Item
                                name="name"
                                label={<Text strong>Tên sản phẩm</Text>}
                                rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}
                            >
                                <Input placeholder="Nhập tên sản phẩm" size="large" style={{ borderRadius: 8 }} />
                            </Form.Item>

                            <Form.Item
                                name="description"
                                label={<Text strong>Mô tả chi tiết</Text>}
                            >
                                <TextArea 
                                    rows={isMobile ? 4 : 6} 
                                    placeholder="Nhập mô tả" 
                                    style={{ borderRadius: 8 }}
                                />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="price"
                                        label={<Text strong>Giá bán (VNĐ)</Text>}
                                    >
                                        <InputNumber
                                            style={{ width: '100%', borderRadius: 8 }}
                                            size="large"
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value?.replace(/\D/g, '') as unknown as number}
                                            placeholder="0"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="status"
                                        label={<Text strong>Tình trạng kho</Text>}
                                        valuePropName="checked"
                                        getValueProps={(value) => ({ checked: value === ProductStatus.IN_STOCK })}
                                        getValueFromEvent={(checked) => checked ? ProductStatus.IN_STOCK : ProductStatus.OUT_OF_STOCK}
                                    >
                                        <Switch 
                                            checkedChildren={<Space><CheckCircleOutlined /> Còn hàng</Space>} 
                                            unCheckedChildren={<Space><StopOutlined /> Hết hàng</Space>}
                                            className="custom-status-switch"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </section>

                        <Divider />

                        <section style={{ marginTop: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <ShoppingOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                                    Tin nhắn nhanh
                                </Title>
                                <Button type="primary" ghost onClick={() => form.setFieldsValue({ quickMessages: [...(form.getFieldValue('quickMessages') || []), ''] })} icon={<PlusOutlined />} size="middle" style={{ borderRadius: 6 }}>
                                    Thêm mẫu tin
                                </Button>
                            </div>
                            
                            <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: isMobile ? 12 : 14 }}>
                                Các mẫu tin nhắn giúp bạn phản hồi khách hàng nhanh hơn khi tư vấn.
                            </Paragraph>

                            <Form.List name="quickMessages">
                                {(fields, { add, remove }) => (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <div key={key} style={{ 
                                                display: 'flex', 
                                                gap: 12, 
                                                alignItems: 'flex-start',
                                                background: '#f8f9fa',
                                                padding: isMobile ? '8px 12px' : '12px 16px',
                                                borderRadius: 12,
                                                border: '1px solid #f0f0f0'
                                            }}>
                                                <Form.Item
                                                    {...restField}
                                                    name={name}
                                                    rules={[{ required: true, message: 'Nội dung không được để trống' }]}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                >
                                                    <TextArea 
                                                        placeholder="Nhập nội dung tin nhắn mẫu..." 
                                                        autoSize={{ minRows: 2, maxRows: 4 }}
                                                        bordered={false}
                                                        style={{ padding: 0, fontSize: 14, background: 'transparent' }}
                                                    />
                                                </Form.Item>
                                                <Button 
                                                    type="text" 
                                                    danger 
                                                    icon={<DeleteOutlined />} 
                                                    onClick={() => remove(name)}
                                                    style={{ marginTop: 4 }}
                                                />
                                            </div>
                                        ))}
                                        {fields.length === 0 && (
                                            <div style={{ 
                                                padding: isMobile ? '24px' : '40px', 
                                                background: '#fcfcfc', 
                                                border: '1px dashed #d9d9d9', 
                                                borderRadius: 12, 
                                                textAlign: 'center' 
                                            }}>
                                                <Paragraph type="secondary" style={{ margin: 0, fontSize: isMobile ? 12 : 14 }}>
                                                    Chưa có mẫu tin nhắn nào. Nhấn "Thêm mẫu tin" để bắt đầu.
                                                </Paragraph>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Form.List>
                        </section>
                    </Col>
                    
                    <Col xs={24} lg={9}>
                        <Card 
                            size="small" 
                            title={<Text strong style={{ fontSize: 16 }}><ShoppingOutlined /> Thư viện hình ảnh</Text>}
                            bordered={false}
                            style={{ 
                                background: '#fafafa', 
                                borderRadius: 16, 
                                minHeight: isMobile ? 'auto' : '100%',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <div style={{ marginBottom: 20 }}>
                                <Upload.Dragger
                                    customRequest={handleUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                    multiple
                                    style={{ 
                                        padding: isMobile ? '16px' : '24px',
                                        background: '#fff',
                                        border: '2px dashed #d9d9d9',
                                        borderRadius: '12px',
                                    }}
                                >
                                    <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                        <PlusOutlined style={{ fontSize: isMobile ? 24 : 32, color: '#1890ff' }} />
                                    </p>
                                    <p className="ant-upload-text" style={{ fontSize: isMobile ? 13 : 14, fontWeight: 500 }}>
                                        Tải ảnh sản phẩm
                                    </p>
                                </Upload.Dragger>
                            </div>

                            <div className="image-grid" style={{ 
                                display: 'grid', 
                                gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(130px, 1fr))' : 'repeat(auto-fill, minmax(130px, 1fr))', 
                                gap: 12,
                                maxHeight: isMobile ? 400 : 600,
                                overflowY: 'auto',
                                paddingRight: 4
                            }}>
                                {images.map((img, index) => (
                                    <div 
                                        key={index} 
                                        className="image-item"
                                        style={{ 
                                            position: 'relative', 
                                            paddingTop: '100%', 
                                            border: img.isPrimary ? '2px solid #1890ff' : '1px solid #e8e8e8',
                                            borderRadius: 12,
                                            overflow: 'hidden',
                                            background: '#fff',
                                            boxShadow: img.isPrimary ? '0 0 0 2px rgba(24, 144, 255, 0.1)' : 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <img 
                                            src={getImageUrl(img.url)} 
                                            alt={`Product ${index}`} 
                                            style={{ 
                                                position: 'absolute', 
                                                top: 0, 
                                                left: 0, 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' 
                                            }} 
                                        />
                                        
                                        {img.isPrimary && (
                                            <Tag color="blue" style={{ position: 'absolute', top: 4, left: 4, margin: 0, fontSize: 10, borderRadius: 4 }}>
                                                Chính
                                            </Tag>
                                        )}

                                        <div className="image-overlay">
                                            <Space size={4} wrap style={{ justifyContent: 'center' }}>
                                                <Button size="small" type="primary" shape="circle" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); setPreviewImage(getImageUrl(img.url)); setPreviewOpen(true); }} />
                                                <Button size="small" type="primary" shape="circle" style={{ background: img.isPrimary ? '#faad14' : undefined }} icon={img.isPrimary ? <StarFilled /> : <StarOutlined />} onClick={(e) => { e.stopPropagation(); handleSetPrimary(index); }} />
                                                <Button size="small" type="primary" shape="circle" icon={<CopyOutlined />} onClick={(e) => { e.stopPropagation(); handleCopyImage(img.url); }} />
                                                <Button size="small" type="primary" danger shape="circle" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); handleDeleteImage(index); }} />
                                            </Space>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                </Row>

                <div style={{ textAlign: isMobile ? 'center' : 'right', marginTop: 24, padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
                    <Button type="primary" htmlType="submit" loading={loading} size="large" style={{ width: isMobile ? '100%' : 150, borderRadius: 8 }}>
                        Lưu sản phẩm
                    </Button>
                </div>
            </Form>

            <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} title="Xem chi tiết ảnh" width={isMobile ? '95%' : 600}>
                <img alt="Preview" style={{ width: '100%' }} src={previewImage} />
            </Modal>

            <style>{`
                .image-item .image-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    opacity: 0;
                    transition: opacity 0.3s;
                }
                .image-item:hover .image-overlay {
                    opacity: 1;
                }
                .custom-status-switch {
                    height: 38px;
                    min-width: 130px;
                    display: inline-flex;
                    align-items: center;
                    border-radius: 20px;
                }
                .custom-status-switch .ant-switch-handle {
                    width: 30px;
                    height: 30px;
                    top: 4px;
                }
                .custom-status-switch.ant-switch-checked .ant-switch-handle {
                    inset-inline-start: calc(100% - 34px);
                }
                .custom-status-switch:not(.ant-switch-checked) .ant-switch-handle {
                    inset-inline-start: 4px;
                }
                .custom-status-switch .ant-switch-inner {
                    display: flex;
                    align-items: center;
                    padding-inline-start: 38px;
                    padding-inline-end: 12px;
                    height: 100%;
                }
                .custom-status-switch.ant-switch-checked .ant-switch-inner {
                    padding-inline-start: 12px;
                    padding-inline-end: 38px;
                }
                .custom-status-switch .ant-switch-inner-checked,
                .custom-status-switch .ant-switch-inner-unchecked {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                    font-weight: 600;
                    font-size: 13px;
                }
                .custom-status-switch.ant-switch-checked {
                    background-color: #52c41a !important;
                }
                .custom-status-switch:not(.ant-switch-checked) {
                    background-color: #ff4d4f !important;
                }
            `}</style>
        </Card>
    );
};
